import {
  PUBLIC_YOTPO_APP_KEY_STORE_US,
  PUBLIC_YOTPO_APP_KEY_STORE_UK,
  PUBLIC_YOTPO_APP_KEY_STORE_EU
} from '$env/static/public';
import { derived, get } from 'svelte/store';
import { trimId } from './utils/shopify/productsId';
import { page } from '$app/stores';
import { region } from './region';
import { constant } from './storyblok-constants';
import { stringToNumber } from './utils/number';

const yotpoKey = derived(region, ($region) => {
  return {
    us: PUBLIC_YOTPO_APP_KEY_STORE_US,
    uk: PUBLIC_YOTPO_APP_KEY_STORE_UK,
    eu: PUBLIC_YOTPO_APP_KEY_STORE_EU
  }[$region];
});

export type CustomField = {
  title: string;
  form_id: number;
  value: string | number;
  field_type: 'SingleChoice' | 'Rating' | 'CustomerFreeText' | 'ProductFreeText';
};

export type ProductImages = {
  images: {
    original_image_url: string;
    tagged_products: Record<string, string>[];
    created_time: string;
    media_type: string;
    post: {
      content: string;
      created_time: string;
      username: string;
    };
  }[];
};

export type Reviews = {
  pagination: {
    page: number;
    per_page: number;
    total: number;
  };
  bottomline: {
    average_score: number;
    total_review: number;
    total_organic_reviews: number;
    organic_average_score: number;
    star_distribution: Record<'1' | '2' | '3' | '4' | '5', number>;
    custom_fields_bottomline: null; // not sure what this is
  };
  products: {
    id: number;
    domain_key: string;
    name: string;
    social_links: Record<string, string>;
    embedded_widget_link: string;
    testimonials_product_link: string;
    product_link: string;
    image_url: string;
  }[];
  reviews: {
    id: number;
    score: number;
    votes_up: number;
    votes_down: number;
    title: string;
    content: string;
    created_at: string;
    verified_buyer: boolean;
    source_review_id: null; // not sure what this is
    sentiment: number;
    user: {
      user_id: number;
      display_name: string;
      social_image: null;
      user_type: 'User';
      is_social_connected: number;
    };
    images_data?: Array<{
      original_url: string;
    }>;
    product_id: number;
    custom_fields: Record<string, CustomField>;
    comment?: Record<string, string>;
  }[];
};

export const getReviewsData = async ({
  id,
  page = 1,
  sort = 'default',
  star
}: {
  id: string;
  page: number;
  sort?: 'default' | 'date' | 'rating';
  star?: '1' | '2' | '3' | '4' | '5';
}): Promise<Reviews> => {
  const params = new URLSearchParams({
    per_page: stringToNumber(constant('reviews_drawer_page_size'), 5).toString(),
    page: page.toString()
  });

  if (sort !== 'default') {
    params.set('sort', sort);
  }

  if (star) params.set('star', star);

  const res = await fetch(
    `https://api.yotpo.com/v1/widget/${get(yotpoKey)}/products/${trimId(
      id
    )}/reviews.json?${params.toString()}`
  );

  if (!res.ok) throw new Error('Failed to fetch reviews');

  const data = await res.json();

  return data.response;
};

export type Bottomline = { bottomline: { average_score: number; total_reviews: number } };
export const getBottomlineData = async (id: string): Promise<Bottomline> => {
  const res = await fetch(
    `https://api.yotpo.com/products/${get(yotpoKey)}/${trimId(id)}/bottomline`
  );

  if (!res.ok) throw new Error('Failed to fetch reviews');

  const data = await res.json();

  return data.response;
};

export const createReview = async (payload: {
  sku: string;
  title: string;
  user: {
    name: string;
    email: string;
  };
  review: {
    score: number;
    title: string;
    content: string;
    customFields: Record<string, FormDataEntryValue>;
  };
}) => {
  const res = await fetch('https://api.yotpo.com/v1/widget/reviews', {
    method: 'POST',
    headers: { accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({
      appkey: get(yotpoKey),
      sku: payload.sku,
      product_title: payload.title,
      product_url: get(page).url.toString().split('?')[0],
      display_name: payload.user.name,
      email: payload.user.email,
      review_title: payload.review.title,
      review_content: payload.review.content,
      review_score: payload.review.score,
      custom_fields: payload.review.customFields
    })
  });

  if (!res.ok) throw new Error('Failed to create review');
};

export const getProductImages = async ({
  id,
  page = 1,
  sort = 'date'
}: {
  id: string;
  page: number;
  sort?: 'date' | 'rating';
}): Promise<ProductImages> => {
  const params = new URLSearchParams({
    per_page: stringToNumber(constant('reviews_drawer_page_size'), 5).toString(),
    page: page.toString(),
    sort
  });

  const res = await fetch(
    `https://api.yotpo.com/v1/widget/${get(yotpoKey)}/albums/product/${trimId(
      id
    )}?${params.toString()}`
  );

  if (!res.ok) throw new Error('Failed to fetch reviews');

  const data = await res.json();

  return data.response;
};
